import { atom, useAtomValue } from "jotai";
import { atomFamily } from "jotai/utils";
import { forwardRef, useMemo } from "react";
import { Button } from "#scripts/components/ui/button.tsx";
import { Ta } from "#scripts/components/ui/icons.tsx";
import { DevState } from "#scripts/lib/dev/DevState.tsx";
import { cn } from "#scripts/lib/utils.ts";
import type { UID } from "#scripts/vmc/WorldState.ts";
import { useWorldState } from "#scripts/world-state-demo/world-state-context.ts";
import { getUIDColor } from "./getUIDColor.tsx";
import { devHoverDetailsAtom, useDevHoverReference } from "./useDevHover.ts";

interface DevEntityDetailsProps extends React.ComponentPropsWithoutRef<"div"> {
  uid: UID;
  detailed?: boolean;
  onExit?: () => void;
}

const getIsOutOfFocusAtom = atomFamily((uid: UID) =>
  atom((get) => {
    const current = get(devHoverDetailsAtom);
    if (current && current.uid !== uid) return true;
    return false;
  }),
);

export const DevEntityDetails = forwardRef<HTMLDivElement, DevEntityDetailsProps>(
  ({ uid, detailed, className, style, onExit, ...props }, ref) => {
    const worldState = useWorldState();
    const entity = useMemo(() => {
      return worldState.getEntityMaybe?.(uid as any) || { _id: uid, _name: "Entity" };
    }, [uid, worldState]);

    const isOutOfFocus = useAtomValue(getIsOutOfFocusAtom(uid));
    const color = getUIDColor(uid);

    const cardStyle = {
      ...color.assocContainer,
      opacity: isOutOfFocus ? 0.5 : 1,
      ...style,
    };

    const idStyle = {
      ...color.textPrimary,
    };

    const devHoverProps = useDevHoverReference(uid);

    return (
      <div
        ref={ref}
        className={cn("rounded flex flex-col bg-bg-elevated text-text border border-border shadow-sm", className)}
        style={cardStyle}
        {...props}
      >
        <div data-loc="phosphor-blog/scripts/world-state-demo/DevEntityDetails.tsx:56:14" data-name="div" className="p-3 text-base sticky top-0 z-10 backdrop-blur-sm bg-bg-elevated/20 font-medium">
          {entity._name || "Entity"}
          <div data-loc="phosphor-blog/scripts/world-state-demo/DevEntityDetails.tsx:58:16" data-name="div" className="text-sm font-mono mt-1" style={idStyle} title={String(uid)} {...devHoverProps}>
            {String(uid)}
          </div>
          {onExit && (
            <Button data-loc="phosphor-blog/scripts/world-state-demo/DevEntityDetails.tsx:62:21" data-name="Button" className="absolute top-2 right-2" onClick={onExit} IconStart={Ta.IconX} size="icon" variant="ghost" />
          )}
        </div>
        <div data-loc="phosphor-blog/scripts/world-state-demo/DevEntityDetails.tsx:65:14" data-name="div" className="px-3 py-2 mt-2 text-sm">
          <span data-loc="phosphor-blog/scripts/world-state-demo/DevEntityDetails.tsx:66:17" data-name="span" className="font-bold">Components:</span>
          <DevState data-loc="phosphor-blog/scripts/world-state-demo/DevEntityDetails.tsx:67:21" data-name="DevState" data={entity} name="Components" />
        </div>
      </div>
    );
  },
);
DevEntityDetails.displayName = "DevEntityDetails";
